import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  CardBody,
  Button,
  Input,
  Select,
  SelectItem,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Chip,
  Tooltip,
  Spinner,
} from "@heroui/react";
import { Plus, Trash, Pencil, Download, Upload, Buildings, Cube, MapTrifold } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { UniversalPickerModal } from "shared/components/UniversalPickerModal";
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { useSession } from '@clerk/clerk-react';
import { API_URL } from "constants/index";
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { toast } from "react-toastify";
import { sortBuildingAttributes } from 'shared/utils/sort';
import {
  UniversalTable,
  UniversalTableSearch,
} from 'shared/components/UniversalTable';
import type {
  UniversalFilterSection,
  UniversalGroupByOption,
  CellConfig,
} from 'shared/components/UniversalTable';
import {
  ATTRIBUTE_STATUS_CONFIG,
  AttributeRentalStatus,
  CONTRACT_PHASE,
  getAttributeRentalStatus,
  getAttributeUnitIds,
} from './buildingAttributeStatus';
import ModalAddContract from "../../buildings/components/rentalTab/modalAddContract";
import { AttributeDocumentsSection } from "shared/components/attributeDocuments/AttributeDocumentsSection";
import { BuildingDTO } from "shared/types/building";
import { UnitDTO } from "shared/types/unit";
import { YallyCompany } from "shared/types/yally-company";

interface BuildingAttributeTemplate {
  id: number;
  category: string;
  ledger: string;
  productTemplates: Array<{
    id: number;
    name: string;
    company: {
      id: number;
      name: string;
    };
  }>;
}

interface ContractProduct {
  id: number;
  name: string;
  contract: {
    id: number;
    phase: string;
    unit?: {
      id: string;
    };
    contractContacts?: Array<{
      id: number;
      contactType: string;
      resource?: {
        id: number;
        name: string;
      };
      relatedContact?: {
        email?: string;
        phone_mobile?: string;
        phone?: string;
      };
    }>;
  };
  price?: Array<{
    id: number;
    amount: number;
    effectiveDate?: string;
    recordDate?: string;
  }>;
}

interface BuildingInfo {
  id: string;
  housenr: string;
  googlePlaceLongAddress: string;
  owner?: boolean;
}

interface BuildingAttribute {
  id: number;
  name: string;
  description: string;
  template: BuildingAttributeTemplate;
  buildings: BuildingInfo[];
  contractProducts?: ContractProduct[];
  length?: number;
  width?: number;
  height?: number;
  /** Odoo analytic account (kostenplaats) used when this attribute is rented out on its own. */
  odooAnalyticAccountName?: string | null;
  /** True when this attribute is placed (pin or contour) on at least one building plan. */
  isOnPlan?: boolean;
}

interface Props {
  clusterId?: number;
}

export const BuildingAttributeManager: React.FC<Props> = () => {
  const { session } = useSession();
  const navigate = useNavigate();
  const [attributes, setAttributes] = useState<BuildingAttribute[]>([]);
  
  // Debug effect to track attributes state changes
  useEffect(() => {
    console.log('📊 Frontend: Attributes state changed:', attributes);
    console.log('📊 Frontend: Number of attributes in state:', attributes.length);
    if (attributes.length > 0) {
      console.log('📊 Frontend: First attribute in state:', attributes[0]);
    }
  }, [attributes]);
  const [templates, setTemplates] = useState<BuildingAttributeTemplate[]>([]);
  const [availableBuildings, setAvailableBuildings] = useState<BuildingInfo[]>([]);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<number | ''>('');
  const [selectedBuildings, setSelectedBuildings] = useState<string[]>([]);
  const [buildingSearchQuery, setBuildingSearchQuery] = useState('');
  const [quickCopyCount, setQuickCopyCount] = useState(1);
  const [quickCopyStartNumber, setQuickCopyStartNumber] = useState(1);

  // Import state
  const [isImporting, setIsImporting] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importedAttributes, setImportedAttributes] = useState<any[]>([]);
  const [importResults, setImportResults] = useState<{ success: any[], errors: any[] } | null>(null);

  // Wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const [attributeReferences, setAttributeReferences] = useState<string[]>([]);
  const [referenceType, setReferenceType] = useState<'number' | 'uppercase' | 'lowercase' | 'custom'>('number');
  const [baseName, setBaseName] = useState('');
  const [baseDescription, setBaseDescription] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  
  // Filtering and search (UniversalTableSearch state)
  const [attributeSearchQuery, setAttributeSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({
    status: [],
    template: [],
    building: [],
    unit: [],
  });
  const [groupBy, setGroupBy] = useState<string>('none');

  // Row selection + export state
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [isExportingRows, setIsExportingRows] = useState(false);
  const [isExportingOverview, setIsExportingOverview] = useState(false);

  // Bulk kostenplaats (Odoo analytic account) editing for the selected rows
  // Kostenplaats picker target: bulk (selected rows) or a single attribute (clicked row). Both use
  // the shared UniversalPickerModal.
  const [kostenplaatsPicker, setKostenplaatsPicker] = useState<
    { mode: 'bulk' } | { mode: 'single'; attribute: BuildingAttribute } | null
  >(null);
  const [isBulkKostenplaatsSaving, setIsBulkKostenplaatsSaving] = useState(false);
  
  // Dimensions for new attributes
  const [dimensions, setDimensions] = useState({
    length: '',
    width: '',
    height: ''
  });

  // Edit state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState<BuildingAttribute | null>(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    length: '',
    width: '',
    height: '',
    odooAnalyticAccountName: ''
  });

  // Attribute-only rental (rent out a vacant attribute on its own, e.g. a parking)
  const [rentBuilding, setRentBuilding] = useState<BuildingDTO | null>(null);
  const [rentAttributeId, setRentAttributeId] = useState<number | undefined>(undefined);
  const [isRentModalOpen, setIsRentModalOpen] = useState(false);
  // When an attribute is linked to several buildings, first ask which one the contract is for.
  const [buildingChoice, setBuildingChoice] = useState<{ attributeId: number; buildings: BuildingInfo[] } | null>(null);
  // Cancel (opzeggen) of an attribute-only contract straight from this page.
  const [cancelTarget, setCancelTarget] = useState<{ contractId: number; attributeName: string } | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  // End date for a future/dated opzeg (empty = "meteen beëindigen").
  const [cancelEndDate, setCancelEndDate] = useState('');
  // Eligibility is checked when the modal opens so we can block the action up-front when the
  // contract also has a unit or other products.
  const [isCheckingCancel, setIsCheckingCancel] = useState(false);
  const [cancelEligibility, setCancelEligibility] = useState<{ cancellable: boolean; reason?: string; buildingId?: string } | null>(null);

  useEffect(() => {
    console.log('🚀 Frontend: BuildingAttributeManager component mounted, loading data...');
    const loadData = async () => {
      setIsLoading(true);
      try {
        console.log('🔄 Frontend: Starting parallel data fetch...');
        await Promise.all([
          fetchAttributes(),
          fetchTemplates(),
          fetchAvailableBuildings()
        ]);
        console.log('✅ Frontend: All data fetched successfully');
      } finally {
        setIsLoading(false);
        console.log('🏁 Frontend: Loading completed');
      }
    };
    loadData();
  }, []);

  const fetchAttributes = async () => {
    console.log('🎯 Frontend: fetchAttributes() called');
    try {
      console.log('📡 Frontend: Making API request to /building-attributes');
      const response = await fetchWithAuth(`${API_URL}/building-attributes`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      }, session);
      
      console.log('📡 Frontend: Response status:', response.status, response.statusText);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch attributes: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('📊 Frontend: Raw API response:', data);
      console.log('📊 Frontend: Data is array:', Array.isArray(data));
      console.log('📊 Frontend: Data length:', data?.length || 0);
      
      if (Array.isArray(data) && data.length > 0) {
        console.log('🏗️ Frontend: First attribute structure:', data[0]);
        
        // Log detailed info for each attribute
        data.forEach((attr, index) => {
          console.log(`🏗️ Frontend Attribute ${index + 1}: "${attr.name}"`);
          console.log(`   - ID: ${attr.id}`);
          console.log(`   - Template: ${attr.template?.category || 'No template'}`);
          console.log(`   - Buildings: ${attr.buildings?.length || 0}`);
          console.log(`   - Contract Products: ${attr.contractProducts?.length || 0}`);
          
          if (attr.contractProducts && attr.contractProducts.length > 0) {
            attr.contractProducts.forEach((cp: any, cpIndex: number) => {
              console.log(`     📦 Frontend Contract Product ${cpIndex + 1}:`);
              console.log(`        - ID: ${cp.id}`);
              console.log(`        - Name: ${cp.name}`);
              console.log(`        - Contract:`, cp.contract);
              console.log(`        - Contract Phase: ${cp.contract?.phase}`);
              console.log(`        - Contract Unit: ${cp.contract?.unit?.id}`);
              console.log(`        - Prices: ${cp.price?.length || 0}`);
              
              if (cp.price && cp.price.length > 0) {
                cp.price.forEach((price: any, priceIndex: number) => {
                  console.log(`           💰 Frontend Price ${priceIndex + 1}: €${price.amount} (effective: ${price.effectiveDate})`);
                });
              }
            });
          }
        });
      }
      
      setAttributes(Array.isArray(data) ? data : []);
      console.log('✅ Frontend: Attributes state updated');
    } catch (error) {
      console.error('❌ Frontend: Error fetching attributes:', error);
      setAttributes([]);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/clusters/templates`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      }, session);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch templates: ${response.statusText}`);
      }
      
      const data = await response.json();
      setTemplates(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplates([]);
    }
  };

  const fetchAvailableBuildings = async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/buildings/simple`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      }, session);

      if (!response.ok) {
        throw new Error(`Failed to fetch buildings: ${response.statusText}`);
      }

      const data = await response.json();
      const buildings = data.buildings || [];
      setAvailableBuildings(buildings);
    } catch (error) {
      console.error('Error fetching buildings:', error);
      setAvailableBuildings([]);
    }
  };

  // Removed heavy owned buildings fetch to keep load fast

  const filteredBuildings = (availableBuildings || []).filter(building =>
    building.id.toLowerCase().includes(buildingSearchQuery.toLowerCase()) ||
    building.housenr.toLowerCase().includes(buildingSearchQuery.toLowerCase()) ||
    building.googlePlaceLongAddress.toLowerCase().includes(buildingSearchQuery.toLowerCase())
  );

  // Enrich each attribute with the scalar fields the UniversalTable needs for
  // grouping (status / building / template). Doing this once here keeps render
  // pure and ensures grouping works without mutating during render.
  const enrichedAttributes = useMemo(() => {
    return (attributes || []).map((attribute) => {
      const status = getAttributeRentalStatus(attribute);
      const primaryBuildingId = attribute.buildings?.[0]?.id || 'Onbekend';
      return {
        ...attribute,
        status,
        building: primaryBuildingId,
        templateCategory: attribute.template?.category || 'Onbekend',
      };
    });
  }, [attributes]);

  const filteredAttributes = useMemo(() => {
    const searchLower = attributeSearchQuery.trim().toLowerCase();
    const statusFilter = activeFilters.status || [];
    const templateFilter = activeFilters.template || [];
    const buildingFilter = activeFilters.building || [];
    const unitFilter = activeFilters.unit || [];

    const filtered = enrichedAttributes.filter((attribute) => {
      // Free-text search across name, description, template, buildings, units, tenants
      if (searchLower) {
        const haystack: string[] = [
          attribute.name || '',
          attribute.description || '',
          attribute.template?.category || '',
          attribute.template?.ledger || '',
          ...(attribute.buildings || []).map((b) => b.id || ''),
          ...getAttributeUnitIds(attribute),
        ];
        const tenantHaystack = (attribute.contractProducts || []).flatMap((cp) =>
          (cp.contract?.contractContacts || []).map((cc: any) => cc?.resource?.name || ''),
        );
        const matchesSearch = [...haystack, ...tenantHaystack]
          .join(' ')
          .toLowerCase()
          .includes(searchLower);
        if (!matchesSearch) return false;
      }

      if (statusFilter.length && !statusFilter.includes(getAttributeRentalStatus(attribute))) {
        return false;
      }

      if (
        templateFilter.length &&
        !templateFilter.includes((attribute as any).template?.id?.toString() || '')
      ) {
        return false;
      }

      if (
        buildingFilter.length &&
        !(attribute.buildings || []).some((b) => buildingFilter.includes(b.id))
      ) {
        return false;
      }

      if (unitFilter.length) {
        const unitIds = getAttributeUnitIds(attribute);
        if (!unitIds.some((id) => unitFilter.includes(id))) return false;
      }

      return true;
    });

    return sortBuildingAttributes(filtered);
  }, [enrichedAttributes, attributeSearchQuery, activeFilters]);

  // Summary table: rows per building, columns per template category with counts
  const summaryCategories = React.useMemo(() => {
    const set = new Set<string>();
    (templates || []).forEach(t => t?.category && set.add(t.category));
    return Array.from(set.values()).sort((a, b) => a.localeCompare(b));
  }, [templates]);

  const summaryHeaderColumns = React.useMemo(() => {
    return [
      { key: 'building', label: 'GEBOUW' },
      ...summaryCategories.map(cat => ({ key: cat, label: cat })),
      { key: 'total', label: 'TOTAAL' }
    ];
  }, [summaryCategories]);

  const summaryBuildings = React.useMemo(() => {
    const fromAvailable = (availableBuildings || []).slice().sort((a, b) => a.id.localeCompare(b.id));
    if (fromAvailable.length > 0) return fromAvailable;
    // Fallback: derive from attributes only
    const map = new Map<string, BuildingInfo>();
    (attributes || []).forEach(a => (a.buildings || []).forEach(b => map.set(b.id, b)));
    return Array.from(map.values()).sort((a, b) => a.id.localeCompare(b.id));
  }, [availableBuildings, attributes]);

  const summaryCounts = React.useMemo(() => {
    const counts: Record<string, Record<string, number>> = {};
    summaryBuildings.forEach(b => {
      counts[b.id] = {};
      summaryCategories.forEach(cat => {
        counts[b.id][cat] = (attributes || []).filter(a => a.template?.category === cat && (a.buildings || []).some(bb => bb.id === b.id)).length;
      });
    });
    return counts;
  }, [attributes, summaryBuildings, summaryCategories]);

  const summaryTotals = React.useMemo(() => {
    const totals: Record<string, number> = {};
    summaryCategories.forEach(cat => {
      totals[cat] = summaryBuildings.reduce((sum, b) => sum + (summaryCounts[b.id]?.[cat] || 0), 0);
    });
    return totals;
  }, [summaryCategories, summaryBuildings, summaryCounts]);

  type SummaryRow = BuildingInfo & { _isTotals?: boolean };
  const summaryItems: SummaryRow[] = React.useMemo(() => {
    const items = [...summaryBuildings] as SummaryRow[];
    items.push({ id: '_totals', housenr: '', googlePlaceLongAddress: '', _isTotals: true } as any);
    return items;
  }, [summaryBuildings]);

  // Totals can be added later if needed

  const generateReferences = (count: number, type: 'number' | 'uppercase' | 'lowercase' | 'custom', startValue: number = 1) => {
    const refs: string[] = [];
    for (let i = 0; i < count; i++) {
      switch (type) {
        case 'number':
          refs.push((startValue + i).toString());
          break;
        case 'uppercase':
          refs.push(String.fromCharCode(65 + i)); // A, B, C...
          break;
        case 'lowercase':
          refs.push(String.fromCharCode(97 + i)); // a, b, c...
          break;
        case 'custom':
          refs.push(`${startValue + i}`);
          break;
      }
    }
    return refs;
  };

  const handleStepNext = () => {
    if (currentStep === 1 && !selectedTemplate) {
      toast.error('Selecteer eerst een template');
      return;
    }
    if (currentStep === 2 && selectedBuildings.length === 0) {
      toast.error('Selecteer minstens één gebouw');
      return;
    }
    if (currentStep === 3 && (!baseName.trim() || quickCopyCount < 1)) {
      toast.error('Voer een naam in en selecteer het aantal attributen');
      return;
    }
    
    if (currentStep === 3) {
      // Generate initial references when moving to step 4
      const refs = generateReferences(quickCopyCount, referenceType, quickCopyStartNumber);
      setAttributeReferences(refs);
    }
    
    setCurrentStep(currentStep + 1);
  };

  const handleStepBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const resetWizard = () => {
    setCurrentStep(1);
    setSelectedTemplate('');
    setSelectedBuildings([]);
    setBuildingSearchQuery('');
    setQuickCopyCount(1);
    setQuickCopyStartNumber(1);
    setBaseName('');
    setBaseDescription('');
    setAttributeReferences([]);
    setReferenceType('number');
    setDimensions({ length: '', width: '', height: '' });
  };

  const handleCreateAttribute = async () => {
    if (!selectedTemplate) {
      toast.error('Selecteer een template');
      return;
    }

    if (selectedBuildings.length === 0) {
      toast.error('Selecteer minstens één gebouw');
      return;
    }

    if (attributeReferences.length === 0) {
      toast.error('Geen attributen om aan te maken');
      return;
    }

    try {
      // Create attributes based on the references
      for (let i = 0; i < attributeReferences.length; i++) {
        const attributeName = attributeReferences.length > 1 
          ? `${baseName} ${attributeReferences[i]}`
          : baseName;

        const response = await fetchWithAuth(`${API_URL}/building-attributes`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: attributeName,
            description: baseDescription,
            template: { id: selectedTemplate },
            buildingIds: selectedBuildings,
            length: dimensions.length ? parseFloat(dimensions.length) : null,
            width: dimensions.width ? parseFloat(dimensions.width) : null,
            height: dimensions.height ? parseFloat(dimensions.height) : null
          })
        }, session);

        if (!response.ok) {
          throw new Error(`Failed to create attribute: ${response.statusText}`);
        }
      }

      await fetchAttributes();
      setIsModalOpen(false);
      resetWizard();
      toast.success(`${attributeReferences.length > 1 ? attributeReferences.length + ' attributen' : 'Attribuut'} succesvol aangemaakt`);
    } catch (error) {
      console.error('Error creating attribute:', error);
      toast.error('Fout bij aanmaken van attribuut');
    }
  };

  const handleDeleteAttribute = async (attributeId: number) => {
    const attribute = attributes.find(attr => attr.id === attributeId);
    
    if (attribute?.contractProducts && attribute.contractProducts.length > 0) {
      const activeContracts = attribute.contractProducts.filter(cp => cp.contract?.phase === 'ACTIVE');
      if (activeContracts.length > 0) {
        toast.error(`Dit attribuut kan niet worden verwijderd omdat het wordt gebruikt in ${activeContracts.length} actieve contracten.`);
        return;
      }
    }
    
    if (!confirm('Weet je zeker dat je dit attribuut wilt verwijderen?')) {
      return;
    }
    
    try {
      await fetchWithAuth(`${API_URL}/building-attributes/${attributeId}`, {
        method: 'DELETE'
      }, session);
      await fetchAttributes();
      toast.success('Attribuut succesvol verwijderd');
    } catch (error) {
      console.error('Error deleting attribute:', error);
      toast.error('Fout bij verwijderen van attribuut');
    }
  };

  const getRentedUnitsInfo = (attribute: BuildingAttribute) => {
    console.log(`🔍 Frontend: getRentedUnitsInfo called for attribute "${attribute.name}"`);
    console.log(`🔍 Frontend: Attribute contract products:`, attribute.contractProducts);
    
    if (!attribute.contractProducts || attribute.contractProducts.length === 0) {
      console.log(`🔍 Frontend: No contract products found for attribute "${attribute.name}"`);
      return { units: [], totalRevenue: 0 };
    }

    console.log(`🔍 Frontend: Found ${attribute.contractProducts.length} contract products`);
    
    const activeContracts = attribute.contractProducts.filter(cp => {
      console.log(`🔍 Frontend: Checking contract product ${cp.id} with phase "${cp.contract?.phase}"`);
      // Include both active contracts and concept contracts for display purposes
      return cp.contract?.phase === 'Lopende contracten' || cp.contract?.phase === 'In concept';
    });

    console.log(`🔍 Frontend: Found ${activeContracts.length} active contracts`);

    const units = activeContracts.map(cp => {
      console.log(`🔍 Frontend: Processing contract product ${cp.id}`);
      console.log(`🔍 Frontend: Contract product prices:`, cp.price);
      
      // Get the most recent price (current effective price)
      let currentPrice = 0;
      if (cp.price && cp.price.length > 0) {
        console.log(`🔍 Frontend: Found ${cp.price.length} prices`);
        console.log(`🔍 Frontend: All prices:`, cp.price);
        
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison
        
        // Filter prices that are effective (effectiveDate <= today)
        const effectivePrices = cp.price.filter(price => {
          if (!price.effectiveDate) return true; // If no effective date, consider it effective
          const effectiveDate = new Date(price.effectiveDate);
          effectiveDate.setHours(0, 0, 0, 0);
          return effectiveDate <= today;
        });
        
        console.log(`🔍 Frontend: Effective prices (${effectivePrices.length}):`, effectivePrices);
        
        if (effectivePrices.length > 0) {
          // Sort by effectiveDate descending, then by id descending to get the most recent
          const sortedPrices = [...effectivePrices].sort((a, b) => {
            if (a.effectiveDate && b.effectiveDate) {
              return new Date(b.effectiveDate).getTime() - new Date(a.effectiveDate).getTime();
            }
            return b.id - a.id;
          });
          currentPrice = sortedPrices[0]?.amount || 0;
          console.log(`🔍 Frontend: Most recent effective price: €${currentPrice} (from price ID: ${sortedPrices[0]?.id})`);
        } else {
          console.log(`🔍 Frontend: No effective prices found for contract product ${cp.id}`);
        }
      } else {
        console.log(`🔍 Frontend: No prices found for contract product ${cp.id}`);
      }

      const unitInfo = {
        unitId: cp.contract.unit?.id || 'Onbekend',
        price: currentPrice
      };
      
      console.log(`🔍 Frontend: Unit info:`, unitInfo);
      return unitInfo;
    });

    const totalRevenue = units.reduce((sum, unit) => sum + unit.price, 0);
    console.log(`🔍 Frontend: Total revenue for attribute "${attribute.name}": €${totalRevenue}`);

    return { units, totalRevenue };
  };

  const renderTotalRevenue = (attribute: BuildingAttribute) => {
    console.log(`💰 Frontend: renderTotalRevenue called for attribute "${attribute.name}"`);
    const { totalRevenue } = getRentedUnitsInfo(attribute);
    console.log(`💰 Frontend: Total revenue for attribute "${attribute.name}": €${totalRevenue}`);
    
    if (totalRevenue === 0) {
      console.log(`💰 Frontend: No revenue to render for attribute "${attribute.name}"`);
      return <span className="text-neutral-secondary-mid text-sm">-</span>;
    }

    // Check if revenue comes from concept contracts
    const hasActiveContracts = attribute.contractProducts?.some(cp => cp.contract?.phase === 'Lopende contracten');
    const hasConceptContracts = attribute.contractProducts?.some(cp => cp.contract?.phase === 'In concept');
    
    const revenueElement = (
      <div className="flex items-center gap-1">
        <span className={`font-medium ${hasActiveContracts ? 'text-moss-primary' : 'text-neutral-secondary-mid'}`}>
          €{totalRevenue.toFixed(2)}
        </span>
        <span className="text-xs text-neutral-secondary-mid">/maand</span>
        {hasConceptContracts && !hasActiveContracts && (
          <span className="text-xs text-brick-primary">(concept)</span>
        )}
      </div>
    );
    
    console.log(`💰 Frontend: Rendered revenue element for attribute "${attribute.name}":`, revenueElement);
    return revenueElement;
  };

  const renderStatusPill = (status: AttributeRentalStatus) => {
    const config = ATTRIBUTE_STATUS_CONFIG[status];
    return (
      <Tooltip content={config.description}>
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium border ${config.softBgClass} ${config.textClass} ${config.borderClass}`}
        >
          <span className={`w-2 h-2 rounded-full ${config.bgClass}`} />
          {config.label}
        </span>
      </Tooltip>
    );
  };

  const handleEditAttribute = (attribute: BuildingAttribute) => {
    setEditingAttribute(attribute);
    setEditFormData({
      name: attribute.name,
      description: attribute.description || '',
      length: attribute.length?.toString() || '',
      width: attribute.width?.toString() || '',
      height: attribute.height?.toString() || '',
      odooAnalyticAccountName: attribute.odooAnalyticAccountName || ''
    });
    setIsEditModalOpen(true);
  };

  const handleUpdateAttribute = async () => {
    if (!editingAttribute) return;

    try {
      const response = await fetchWithAuth(`${API_URL}/building-attributes/${editingAttribute.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editFormData.name,
          description: editFormData.description,
          length: editFormData.length ? parseFloat(editFormData.length) : null,
          width: editFormData.width ? parseFloat(editFormData.width) : null,
          height: editFormData.height ? parseFloat(editFormData.height) : null,
          odooAnalyticAccountName: editFormData.odooAnalyticAccountName?.trim() || null,
          template: { id: editingAttribute.template.id }
        })
      }, session);

      if (!response.ok) {
        throw new Error(`Failed to update attribute: ${response.statusText}`);
      }

      await fetchAttributes();
      setIsEditModalOpen(false);
      setEditingAttribute(null);
      setEditFormData({ name: '', description: '', length: '', width: '', height: '', odooAnalyticAccountName: '' });
      toast.success('Attribuut succesvol bijgewerkt');
    } catch (error) {
      console.error('Error updating attribute:', error);
      toast.error('Fout bij bijwerken van attribuut');
    }
  };

  // Fetch the full building (company + units) and open the add-contract modal in attribute-only mode.
  const loadBuildingAndOpenRentModal = async (buildingId: string, attributeId: number) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/building/${buildingId}?contracts=false`,
        { method: 'GET' },
        session,
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch building: ${response.statusText}`);
      }
      const building = (await response.json()) as BuildingDTO;
      if (!building?.company) {
        toast.error('Dit gebouw heeft geen gekoppeld bedrijf — contract kan niet worden aangemaakt.');
        return;
      }
      setRentBuilding(building);
      setRentAttributeId(attributeId);
      setBuildingChoice(null);
      setIsRentModalOpen(true);
    } catch (error) {
      console.error('Error loading building for rental:', error);
      toast.error('Fout bij ophalen van gebouw voor verhuur');
    }
  };

  // Decide how to start renting out an attribute based on how many buildings it is linked to.
  const handleStartRent = (attribute: BuildingAttribute) => {
    const buildings = attribute.buildings || [];
    if (buildings.length === 0) {
      toast.error('Koppel eerst een gebouw aan dit attribuut voordat je het verhuurt.');
      return;
    }
    if (buildings.length === 1) {
      loadBuildingAndOpenRentModal(buildings[0].id, attribute.id);
      return;
    }
    setBuildingChoice({ attributeId: attribute.id, buildings });
  };

  const handleCloseRentModal = () => {
    setIsRentModalOpen(false);
    setRentBuilding(null);
    setRentAttributeId(undefined);
  };

  // Find the id of the currently-running (concept/active) contract linked to this attribute.
  const getCancellableContractId = (attribute: BuildingAttribute): number | undefined => {
    const cp = (attribute.contractProducts || []).find(
      (p) => p.contract?.phase === CONTRACT_PHASE.CONCEPT || p.contract?.phase === CONTRACT_PHASE.ACTIVE,
    );
    return cp?.contract?.id;
  };

  // Open the plan where this attribute is placed (jumps to the building's Plattegrond tab, focused
  // on the attribute). If it sits on several plans we open the first and the others are reachable there.
  const handleOpenPlan = async (attribute: BuildingAttribute) => {
    try {
      const res = await fetchWithAuth(
        `${API_URL}/building-plans/by-attribute/${attribute.id}`,
        { method: 'GET' },
        session,
      );
      const plans = res.ok ? await res.json() : [];
      const plan = plans[0];
      const buildingId = plan?.building?.id;
      if (!plan || !buildingId) {
        toast.info('Dit attribuut staat (nog) niet op een plattegrond.');
        return;
      }
      if (plans.length === 1) {
        // One plan → open it directly, focused on the attribute.
        navigate(`/building/${buildingId}/plattegrond?plan=${plan.id}&attribute=${attribute.id}`);
      } else {
        // Several plans → open the Plattegrond overview filtered to this attribute.
        navigate(`/building/${buildingId}/plattegrond?attribute=${attribute.id}`);
      }
    } catch (error) {
      console.error('Error opening plan for attribute:', error);
      toast.error('Fout bij openen van de plattegrond.');
    }
  };

  const handleStartCancel = async (attribute: BuildingAttribute) => {
    const contractId = getCancellableContractId(attribute);
    if (!contractId) {
      toast.error('Geen lopend contract gevonden voor dit attribuut.');
      return;
    }
    setCancelTarget({ contractId, attributeName: attribute.name });
    // Check up-front whether the contract can be cancelled here (no unit, single product). If not,
    // the modal blocks the action and explains it must be done via the Verhuur tab.
    setCancelEligibility(null);
    setIsCheckingCancel(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/contract/${contractId}/attribute-only-cancellable`,
        { method: 'GET' },
        session,
      );
      if (response.ok) {
        setCancelEligibility(await response.json());
      } else {
        setCancelEligibility({ cancellable: false, reason: 'Kon de opzegbaarheid niet bepalen. Probeer het opnieuw.' });
      }
    } catch (error) {
      console.error('Error checking cancel eligibility:', error);
      setCancelEligibility({ cancellable: false, reason: 'Kon de opzegbaarheid niet bepalen. Probeer het opnieuw.' });
    } finally {
      setIsCheckingCancel(false);
    }
  };

  const resetCancelState = () => {
    setCancelTarget(null);
    setCancelEligibility(null);
    setCancelEndDate('');
  };

  // Calls the guarded backend endpoint. With an endDate the contract goes into opzeg with that date
  // (now or future); without one it is terminated immediately. The backend refuses (400) when the
  // contract also has a unit or other products, in which case we show its message.
  const doCancel = async (endDate?: string) => {
    if (!cancelTarget) return;
    setIsCancelling(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/contract/${cancelTarget.contractId}/cancel-attribute-only`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(endDate ? { endDate } : {}),
        },
        session,
      );

      if (!response.ok) {
        let message = 'Opzeggen mislukt.';
        try {
          const data = await response.json();
          if (data?.message) message = Array.isArray(data.message) ? data.message.join(' ') : data.message;
        } catch {
          // keep default message
        }
        toast.error(message);
        return;
      }

      toast.success(endDate ? 'Opzeg ingesteld.' : 'Contract beëindigd.');
      resetCancelState();
      await fetchAttributes();
    } catch (error) {
      console.error('Error cancelling attribute-only contract:', error);
      toast.error('Fout bij opzeggen van het contract.');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleCancelInFuture = () => {
    if (!cancelEndDate) {
      toast.error('Selecteer een einddatum.');
      return;
    }
    doCancel(cancelEndDate);
  };

  const handleCancelImmediately = () => doCancel();

  const handleDownloadTemplate = async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/excel/building-attribute-template`, {
        method: 'GET'
      }, session);

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'building_attributes_template.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('Template gedownload');
      } else {
        toast.error('Fout bij downloaden van template');
      }
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Fout bij downloaden van template');
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetchWithAuth(`${API_URL}/excel/import-building-attributes`, {
        method: 'POST',
        body: formData,
      }, session);

      if (response.ok) {
        const data = await response.json();
        if (data.attributes && data.attributes.length > 0) {
          setImportedAttributes(data.attributes);
          setIsImportModalOpen(true);
          toast.success(`${data.attributes.length} attributen gelezen uit Excel bestand`);
        } else {
          toast.warning('Geen attributen gevonden in het Excel bestand');
        }
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Fout bij het importeren van attributen');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Fout bij het importeren van attributen');
    } finally {
      setIsImporting(false);
      event.target.value = '';
    }
  };

  const handleImportAttributes = async () => {
    setIsImporting(true);
    try {
      const response = await fetchWithAuth(`${API_URL}/building-attributes/bulk-import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: importedAttributes }),
      }, session);

      if (response.ok) {
        const results = await response.json();
        setImportResults(results);
        toast.success(`${results.success.length} attributen succesvol geïmporteerd`);
        if (results.errors.length > 0) {
          toast.warning(`${results.errors.length} attributen hadden fouten`);
        }

        // Download Excel with results
        try {
          const excelResponse = await fetchWithAuth(`${API_URL}/building-attributes/generate-import-excel`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              originalAttributes: importedAttributes,
              importResults: results
            }),
          }, session);

          if (excelResponse.ok) {
            const blob = await excelResponse.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            const contentDisposition = excelResponse.headers.get('Content-Disposition');
            let filename = 'building_attributes_import_results.xlsx';
            if (contentDisposition) {
              const matches = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
              if (matches != null && matches[1]) {
                filename = matches[1].replace(/['"]/g, '');
              }
            }
            
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            toast.success("Excel bestand met attribuut ID's gedownload");
          }
        } catch (excelError) {
          console.error("Error downloading Excel file:", excelError);
        }

        await fetchAttributes();
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Fout bij het importeren van attributen');
      }
    } catch (error) {
      console.error('Error importing attributes:', error);
      toast.error('Fout bij het importeren van attributen');
    } finally {
      setIsImporting(false);
    }
  };

  // ============================================================
  // Excel export handlers
  // ============================================================
  const downloadBlobAs = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Inline-save the kostenplaats for a single attribute (from the table column, on blur / Enter).
  const handleSaveKostenplaatsInline = async (attribute: BuildingAttribute, rawValue: string) => {
    const value = rawValue.trim();
    if (value === (attribute.odooAnalyticAccountName || '')) return; // no change
    try {
      const response = await fetchWithAuth(
        `${API_URL}/building-attributes/${attribute.id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ odooAnalyticAccountName: value || null }),
        },
        session,
      );
      if (!response.ok) throw new Error(response.statusText);
      // Keep local state in sync so the value persists across re-renders.
      setAttributes((prev) =>
        prev.map((a) => (a.id === attribute.id ? { ...a, odooAnalyticAccountName: value || null } : a)),
      );
      toast.success('Kostenplaats opgeslagen');
    } catch (error) {
      console.error('Error saving kostenplaats:', error);
      toast.error('Fout bij opslaan van kostenplaats');
    }
  };

  // Apply one kostenplaats (Odoo analytic account) to all selected attributes in a single call.
  // Invoked from the UniversalPickerModal (text-input mode).
  const handleBulkSaveKostenplaats = async (value: string) => {
    const ids = Array.from(selectedRows);
    if (ids.length === 0) return;
    const kostenplaats = (value || '').trim();

    setIsBulkKostenplaatsSaving(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/building-attributes/analytic-account/bulk`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ids, odooAnalyticAccountName: kostenplaats || null }),
        },
        session,
      );

      if (!response.ok) {
        throw new Error(`Failed to bulk update kostenplaats: ${response.statusText}`);
      }

      toast.success(`Kostenplaats ingesteld voor ${ids.length} attribu${ids.length === 1 ? 'ut' : 'ten'}.`);
      setKostenplaatsPicker(null);
      setSelectedRows(new Set());
      await fetchAttributes();
    } catch (error) {
      console.error('Error bulk updating kostenplaats:', error);
      toast.error('Fout bij instellen van de kostenplaats.');
    } finally {
      setIsBulkKostenplaatsSaving(false);
    }
  };

  // Single source of truth for the kostenplaats picker confirm (single row or bulk).
  const handleKostenplaatsPickerConfirm = async (value: string) => {
    if (!kostenplaatsPicker) return;
    if (kostenplaatsPicker.mode === 'bulk') {
      await handleBulkSaveKostenplaats(value);
    } else {
      await handleSaveKostenplaatsInline(kostenplaatsPicker.attribute, value);
      setKostenplaatsPicker(null);
    }
  };

  const handleExportSelectedRows = async () => {
    const ids =
      selectedRows.size > 0
        ? Array.from(selectedRows)
        : filteredAttributes.map((a) => a.id);

    if (ids.length === 0) {
      toast.warning('Geen attributen om te exporteren');
      return;
    }

    setIsExportingRows(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/excel/export-building-attributes`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributeIds: ids }),
        },
        session,
      );

      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || `Export failed: ${response.statusText}`);
      }

      const blob = await response.blob();
      downloadBlobAs(
        blob,
        `gebouw-attributen-${new Date().toISOString().split('T')[0]}.xlsx`,
      );
      toast.success(`${ids.length} attribut${ids.length === 1 ? '' : 'en'} geëxporteerd`);
    } catch (error) {
      console.error('Error exporting building attributes:', error);
      toast.error('Fout bij exporteren naar Excel');
    } finally {
      setIsExportingRows(false);
    }
  };

  const handleExportOverview = async () => {
    setIsExportingOverview(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/excel/export-building-attributes-overview`,
        { method: 'GET' },
        session,
      );

      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || `Export failed: ${response.statusText}`);
      }

      const blob = await response.blob();
      downloadBlobAs(
        blob,
        `gebouw-attributen-overzicht-${new Date().toISOString().split('T')[0]}.xlsx`,
      );
      toast.success('Overzicht geëxporteerd');
    } catch (error) {
      console.error('Error exporting overview:', error);
      toast.error('Fout bij exporteren van overzicht');
    } finally {
      setIsExportingOverview(false);
    }
  };

  // ============================================================
  // UniversalTable configuration
  // ============================================================
  const ATTRIBUTE_COLUMNS = useMemo(
    () => [
      { id: 'checkbox', label: null, minWidth: 40, defaultWeight: 0, align: 'center' as const },
      { id: 'status', label: 'Status', minWidth: 100, defaultWeight: 0, sortKey: 'status' },
      { id: 'name', label: 'Naam', minWidth: 150, defaultWeight: 4, sortKey: 'name' },
      { id: 'template', label: 'Template', minWidth: 120, defaultWeight: 2, sortKey: 'template' },
      { id: 'buildings', label: 'Gebouwen', minWidth: 120, defaultWeight: 2 },
      { id: 'units', label: 'Units', minWidth: 110, defaultWeight: 2 },
      { id: 'tenant', label: 'Verhuurd aan', minWidth: 150, defaultWeight: 3 },
      { id: 'contracts', label: 'Contracten', minWidth: 90, defaultWeight: 1 },
      { id: 'rent', label: 'Huurprijs', minWidth: 90, defaultWeight: 1 },
      { id: 'kostenplaats', label: 'Kostenplaats (Odoo)', minWidth: 200, defaultWeight: 2 },
      { id: 'actions', label: 'Acties', minWidth: 170, defaultWeight: 0 },
    ],
    [],
  );

  const filterSections: UniversalFilterSection[] = useMemo(
    () => [
      {
        key: 'status',
        label: 'Status',
        multiSelect: true,
        color: 'moss' as const,
        options: [
          { key: 'rented', label: ATTRIBUTE_STATUS_CONFIG.rented.label },
          { key: 'termination', label: ATTRIBUTE_STATUS_CONFIG.termination.label },
          { key: 'vacant', label: ATTRIBUTE_STATUS_CONFIG.vacant.label },
        ],
      },
      {
        key: 'template',
        label: 'Template',
        multiSelect: true,
        color: 'brick' as const,
        options: templates.map((t) => ({ key: t.id.toString(), label: t.category })),
      },
      {
        key: 'building',
        label: 'Gebouw',
        multiSelect: true,
        color: 'neutral' as const,
        options: [],
        extractOptionsFromData: (rows: BuildingAttribute[]) => {
          const ids = rows.flatMap((a) => (a.buildings || []).map((b) => b.id)).filter(Boolean);
          return Array.from(new Set(ids)).sort().map((id) => ({ key: id, label: id }));
        },
      },
      {
        key: 'unit',
        label: 'Unit',
        multiSelect: true,
        color: 'neutral' as const,
        options: [],
        extractOptionsFromData: (rows: BuildingAttribute[]) => {
          const ids = rows.flatMap((a) => getAttributeUnitIds(a));
          return Array.from(new Set(ids)).sort().map((id) => ({ key: id, label: id }));
        },
      },
    ],
    [templates],
  );

  const groupByOptions: UniversalGroupByOption[] = useMemo(
    () => [
      { key: 'none', label: 'Geen groepering' },
      { key: 'status', label: 'Per status' },
      { key: 'building', label: 'Per gebouw' },
      { key: 'templateCategory', label: 'Per template' },
    ],
    [],
  );

  const renderAttributeRow = useCallback(
    (
      attribute: BuildingAttribute,
      context?: { isSelected: boolean; onToggleSelect: (selected: boolean) => void },
    ): CellConfig[] => {
      const status = getAttributeRentalStatus(attribute);
      const unitIds = getAttributeUnitIds(attribute);
      const buildingIdsList = (attribute.buildings || []).map((b) => b.id);
      const primaryBuildingId = buildingIdsList[0] || '';

      // Only consider products tied to a still-running contract (concept / active / in opzeg).
      // A CLOSED contract must not surface its old tenant or contract number — otherwise a vacant
      // attribute would keep showing the previous tenant.
      const liveContractProducts = (attribute.contractProducts || []).filter(
        (cp) =>
          cp.contract?.phase === CONTRACT_PHASE.CONCEPT ||
          cp.contract?.phase === CONTRACT_PHASE.ACTIVE ||
          cp.contract?.phase === CONTRACT_PHASE.TERMINATION,
      );

      const tenants = liveContractProducts
        .flatMap((cp) => cp.contract?.contractContacts || [])
        .filter((cc: any) => cc?.contactType === 'Contractant (huurder)');

      const contractIds = liveContractProducts
        .map((cp) => cp.contract?.id)
        .filter((id): id is number => typeof id === 'number');

      return [
        // Selection checkbox
        {
          id: 'checkbox',
          align: 'center',
          content: (
            <input
              type="checkbox"
              aria-label={`Selecteer ${attribute.name}`}
              checked={context?.isSelected ?? false}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => context?.onToggleSelect(e.target.checked)}
              className="h-4 w-4 cursor-pointer accent-moss-primary"
            />
          ),
        },
        // Status pill
        {
          id: 'status',
          content: renderStatusPill(status),
        },
        // Name + description
        {
          id: 'name',
          content: (
            <div>
              <div className="font-semibold text-neutral-primary">{attribute.name}</div>
              {attribute.description && (
                <div className="text-sm text-neutral-secondary-mid line-clamp-2">
                  {attribute.description}
                </div>
              )}
              {(attribute.length || attribute.width || attribute.height) && (
                <div className="mt-1 text-xs text-neutral-secondary-mid">
                  {[
                    attribute.length ? `L ${attribute.length}m` : null,
                    attribute.width ? `B ${attribute.width}m` : null,
                    attribute.height ? `H ${attribute.height}m` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              )}
            </div>
          ),
        },
        // Template
        {
          id: 'template',
          content: (
            <div className="space-y-1">
              <Chip
                size="sm"
                className="bg-brick-primary text-brick-secondary-fine"
                variant="flat"
              >
                {attribute.template?.category}
              </Chip>
              {attribute.template?.ledger && (
                <div className="text-xs text-neutral-secondary-mid">
                  Grootboek: {attribute.template.ledger}
                </div>
              )}
            </div>
          ),
        },
        // Buildings
        {
          id: 'buildings',
          content:
            buildingIdsList.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {buildingIdsList.slice(0, 3).map((id) => (
                  <Chip
                    key={id}
                    size="sm"
                    variant="flat"
                    className="bg-white border border-moss-primary text-moss-primary"
                    startContent={<Buildings weight="duotone" size={14} />}
                  >
                    {id}
                  </Chip>
                ))}
                {buildingIdsList.length > 3 && (
                  <Chip
                    size="sm"
                    variant="flat"
                    className="bg-white border border-neutral-secondary-mid text-neutral-secondary-mid"
                  >
                    +{buildingIdsList.length - 3}
                  </Chip>
                )}
              </div>
            ) : (
              <span className="text-neutral-secondary-mid text-sm">—</span>
            ),
        },
        // Units
        {
          id: 'units',
          content:
            unitIds.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {unitIds.slice(0, 3).map((id) => (
                  <Chip
                    key={id}
                    size="sm"
                    variant="flat"
                    className="bg-white border border-moss-primary text-moss-primary"
                    startContent={<Cube weight="duotone" size={14} />}
                  >
                    {id}
                  </Chip>
                ))}
                {unitIds.length > 3 && (
                  <Chip
                    size="sm"
                    variant="flat"
                    className="bg-white border border-neutral-secondary-mid text-neutral-secondary-mid"
                  >
                    +{unitIds.length - 3}
                  </Chip>
                )}
              </div>
            ) : (
              <span className="text-neutral-secondary-mid text-sm">—</span>
            ),
        },
        // Tenant
        {
          id: 'tenant',
          content:
            tenants.length > 0 ? (
              <div className="text-sm leading-tight space-y-1 max-w-[220px]">
                {tenants.slice(0, 2).map((cc: any, idx: number) => {
                  const firstName = cc.relatedContact?.firstName || '';
                  const lastName = cc.relatedContact?.lastName || '';
                  const fallbackName = cc.resource?.name || '';
                  const displayName =
                    [firstName, lastName].filter(Boolean).join(' ') || fallbackName;
                  const email = cc.relatedContact?.email || '';
                  return (
                    <div key={idx}>
                      <div className="font-medium text-neutral-primary truncate">
                        {displayName || '—'}
                      </div>
                      {email && (
                        <div className="text-xs text-neutral-secondary-mid truncate">{email}</div>
                      )}
                    </div>
                  );
                })}
                {tenants.length > 2 && (
                  <div className="text-xs text-neutral-secondary-mid">
                    +{tenants.length - 2} meer
                  </div>
                )}
              </div>
            ) : (
              <span className="text-neutral-secondary-mid text-sm">—</span>
            ),
        },
        // Contracts
        {
          id: 'contracts',
          content:
            contractIds.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {contractIds.map((id, idx) =>
                  primaryBuildingId ? (
                    <a
                      key={`${id}-${idx}`}
                      href={`/building/${primaryBuildingId}#verhuur`}
                      className="text-sm text-moss-primary hover:underline"
                      title={`Contract ${id}`}
                    >
                      #{id}
                    </a>
                  ) : (
                    <span key={`${id}-${idx}`} className="text-sm text-neutral-primary">
                      #{id}
                    </span>
                  ),
                )}
              </div>
            ) : (
              <span className="text-neutral-secondary-mid text-sm">—</span>
            ),
        },
        // Rent
        {
          id: 'rent',
          content: renderTotalRevenue(attribute),
        },
        // Kostenplaats — click to set/change via the picker (single row).
        {
          id: 'kostenplaats',
          content: (
            <button
              type="button"
              title="Klik om de kostenplaats in te stellen"
              onClick={(e) => {
                e.stopPropagation();
                setKostenplaatsPicker({ mode: 'single', attribute });
              }}
              className="w-full truncate rounded border border-transparent px-2 py-1 text-left text-sm hover:border-neutral-primary/20 hover:bg-white"
            >
              {attribute.odooAnalyticAccountName ? (
                <span className="text-neutral-primary">{attribute.odooAnalyticAccountName}</span>
              ) : (
                <span className="italic text-neutral-secondary-mid">Kostenplaats instellen…</span>
              )}
            </button>
          ),
        },
        // Actions
        {
          id: 'actions',
          content: (
            <div className="flex gap-1">
              {getAttributeRentalStatus(attribute) === 'vacant' && (
                <Tooltip content="Verhuren (los gebouwattribuut)">
                  <Button
                    size="sm"
                    variant="flat"
                    className="bg-moss-primary text-brick-secondary-fine"
                    onPress={() => handleStartRent(attribute)}
                  >
                    Verhuren
                  </Button>
                </Tooltip>
              )}
              {getAttributeRentalStatus(attribute) === 'rented' && (
                <Tooltip content="Contract opzeggen">
                  <Button
                    size="sm"
                    variant="flat"
                    color="danger"
                    onPress={() => handleStartCancel(attribute)}
                  >
                    Opzeggen
                  </Button>
                </Tooltip>
              )}
              {attribute.isOnPlan && (
                <Tooltip content="Toon op plattegrond">
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    onPress={() => handleOpenPlan(attribute)}
                  >
                    <MapTrifold size={16} weight="duotone" className="text-moss-primary" />
                  </Button>
                </Tooltip>
              )}
              <Tooltip content="Bewerken">
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  onPress={() => handleEditAttribute(attribute)}
                >
                  <Pencil size={16} className="text-brick-primary" />
                </Button>
              </Tooltip>
              <Tooltip content="Verwijderen">
                <Button
                  isIconOnly
                  size="sm"
                  color="danger"
                  variant="light"
                  onPress={() => handleDeleteAttribute(attribute.id)}
                >
                  <Trash size={16} />
                </Button>
              </Tooltip>
            </div>
          ),
        },
      ];
    },
    // handlers are defined inside the component scope; intentionally not added to deps
    // to avoid re-renders cycling — they remain stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  if (isLoading || !templates || !availableBuildings) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-neutral-secondary-mid">Laden...</div>
      </div>
    );
  }

  console.log('🏪 Frontend: Component rendering with:', {
    attributesLength: attributes.length,
    filteredAttributesLength: filteredAttributes.length,
    isLoading,
    templates: templates.length,
    availableBuildings: availableBuildings.length
  });

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Gebouw Attributen &mdash; Yally</title>
        </Helmet>
      </HelmetProvider>
      <div className="container mx-auto p-6 space-y-6">
        {/* Summary table per gebouw x template */}
        <Card className="bg-brick-secondary-fine">
          <CardBody className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-neutral-secondary-mid text-sm">{isSummaryExpanded ? 'Volledig overzicht' : 'Totaaloverzicht'}</div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="flat"
                  startContent={isExportingOverview ? <Spinner size="sm" /> : <Download size={16} />}
                  isDisabled={isExportingOverview}
                  onPress={handleExportOverview}
                  className="bg-moss-primary text-brick-secondary-fine"
                >
                  {isExportingOverview ? 'Exporteren...' : 'Exporteer overzicht'}
                </Button>
                <Button size="sm" variant="flat" onPress={() => setIsSummaryExpanded(v => !v)}>
                  {isSummaryExpanded ? 'Inklappen' : 'Uitklappen'}
                </Button>
              </div>
            </div>
            <Table
              aria-label="Overzicht per gebouw en template"
              removeWrapper
              className="bg-brick-secondary-fine"
              classNames={{
                wrapper: "shadow-none rounded-lg",
                th: "bg-neutral-primary text-brick-secondary-fine",
                td: "border-b border-neutral-primary/10"
              }}
            >
              <TableHeader columns={summaryHeaderColumns}>
                {(column: any) => (
                  <TableColumn key={column.key}>
                    <div className="flex items-center justify-center h-10 sm:h-12">
                      <span className="text-xs sm:text-sm font-medium px-2">{column.key === 'building' ? 'GEBOUW' : column.label}</span>
                    </div>
                  </TableColumn>
                )}
              </TableHeader>
              <TableBody items={isSummaryExpanded ? summaryItems : (summaryItems.filter(i => i._isTotals))} emptyContent="Geen gebouwen">
                {(b: any) => (
                  <TableRow key={b.id} onClick={() => { if (b._isTotals) setIsSummaryExpanded(v => !v); }}>
                    {(columnKey: React.Key) => (
                      <TableCell>
                        {(() => {
                          if (columnKey === 'building') {
                            const isTotals = !!b._isTotals;
                            return (
                              <div className={`text-center text-sm font-medium ${isTotals ? 'cursor-pointer hover:underline' : ''}`}>
                                {isTotals ? 'Totaal' : b.id}
                              </div>
                            );
                          } else if (columnKey === 'total') {
                            const rowTotal = b._isTotals
                              ? Object.values(summaryTotals || {}).reduce((sum: number, v: any) => sum + (Number(v) || 0), 0)
                              : summaryCategories.reduce((sum, cat) => sum + (summaryCounts[b.id]?.[cat] || 0), 0);
                            const highlight = rowTotal > 0;
                            return (
                              <div className="text-center">
                                <span className={`inline-block rounded-sm px-2 py-0.5 text-sm ${highlight ? 'bg-moss-primary/10' : 'bg-transparent'}`}>{rowTotal}</span>
                              </div>
                            );
                          }
                          const val = b._isTotals ? (summaryTotals[String(columnKey)] || 0) : (summaryCounts[b.id]?.[String(columnKey)] || 0);
                          const bg = val > 0 ? 'bg-moss-primary/10' : 'bg-transparent';
                          return (
                            <div className="text-center">
                              <span className={`inline-block rounded-sm px-2 py-0.5 text-sm ${bg}`}>{val}</span>
                            </div>
                          );
                        })()}
                      </TableCell>
                    )}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardBody>
        </Card>

        <div className="flex justify-between items-center">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold text-neutral-primary">Gebouw Attributen</h1>
            <p className="text-neutral-secondary-mid">Beheer attributen die toegevoegd kunnen worden aan gebouwen</p>
          </div>
          <div className="flex gap-2">
            <Button
              color="secondary"
              variant="flat"
              startContent={<Download />}
              onPress={handleDownloadTemplate}
              className="bg-moss-primary text-brick-secondary-fine"
            >
              Template downloaden
            </Button>
            <label htmlFor="building-attribute-file-upload">
              <Button
                color="secondary"
                variant="flat"
                startContent={isImporting ? <Spinner size="sm" /> : <Upload />}
                isDisabled={isImporting}
                className="bg-neutral-primary text-brick-secondary-fine cursor-pointer"
                as="span"
              >
                {isImporting ? 'Importeren...' : 'Excel importeren'}
              </Button>
            </label>
            <input
              id="building-attribute-file-upload"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
              className="hidden"
              disabled={isImporting}
            />
            <Button
              color="primary"
              startContent={<Plus />}
              onPress={() => setIsModalOpen(true)}
              className="bg-brick-primary text-brick-secondary-fine"
            >
              Attribuut toevoegen
            </Button>
          </div>
        </div>

        {/* Search, filters & grouping */}
        <UniversalTableSearch
          rows={attributes}
          searchValue={attributeSearchQuery}
          onSearchChange={setAttributeSearchQuery}
          searchPlaceholder="Zoek op attribuut, gebouw, unit of huurder..."
          filterSections={filterSections}
          activeFilters={activeFilters}
          onFilterChange={(key, values) =>
            setActiveFilters((prev) => ({ ...prev, [key]: values }))
          }
          groupByOptions={groupByOptions}
          selectedGroupBy={groupBy}
          onGroupByChange={setGroupBy}
        />

        <div className="flex items-center justify-between gap-2 text-sm text-neutral-secondary-mid">
          <div className="flex items-center gap-2">
            <Chip variant="flat" className="bg-brick-primary text-brick-secondary-fine">
              {filteredAttributes.length} resultaten
            </Chip>
            {selectedRows.size > 0 && (
              <>
                <Chip variant="flat" className="bg-moss-primary text-brick-secondary-fine">
                  {selectedRows.size} geselecteerd
                </Chip>
                <Button
                  size="sm"
                  variant="flat"
                  className="bg-brick-primary text-brick-secondary-fine"
                  onPress={() => setKostenplaatsPicker({ mode: 'bulk' })}
                >
                  Kostenplaats instellen
                </Button>
                <Button
                  size="sm"
                  variant="light"
                  onPress={() => setSelectedRows(new Set())}
                >
                  Selectie wissen
                </Button>
              </>
            )}
          </div>
          <Button
            size="sm"
            variant="flat"
            startContent={isExportingRows ? <Spinner size="sm" /> : <Download size={16} />}
            isDisabled={isExportingRows || filteredAttributes.length === 0}
            onPress={handleExportSelectedRows}
            className="bg-moss-primary text-brick-secondary-fine"
          >
            {isExportingRows
              ? 'Exporteren...'
              : selectedRows.size > 0
                ? `Exporteer ${selectedRows.size} rij${selectedRows.size === 1 ? '' : 'en'}`
                : 'Exporteer alle resultaten'}
          </Button>
        </div>

        {/* Attributes table */}
        <UniversalTable
          rows={filteredAttributes}
          title="Gebouw attributen"
          columnsConfig={{ columns: ATTRIBUTE_COLUMNS }}
          groupBy={groupBy === 'none' ? undefined : groupBy}
          renderRow={renderAttributeRow}
          getRowKey={(row) => row.id}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
        />
      </div>
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="2xl" scrollBehavior="inside">
  <ModalContent>
    <ModalHeader className="flex items-center gap-2">
      <span>Nieuw attribuut toevoegen</span>
      <Chip size="sm" color="primary" variant="flat" className="bg-brick-primary text-brick-secondary-fine">
        Stap {currentStep} van 4
      </Chip>
    </ModalHeader>
    <ModalBody>
      {/* Step 1: Select Template */}
      {currentStep === 1 && (
        <div className="space-y-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-neutral-primary mb-2">
              Welk type attribuut wil je aanmaken?
            </h3>
            <p className="text-neutral-secondary-mid">Selecteer een template voor het attribuut</p>
          </div>
          
          <div className="grid grid-cols-1 gap-3">
            {templates && templates.length > 0 ? templates.map((template) => (
              <Card 
                key={template.id} 
                isPressable
                className={`cursor-pointer transition-all ${
                  selectedTemplate === template.id 
                    ? 'bg-brick-primary text-brick-secondary-fine ring-2 ring-brick-primary' 
                    : 'bg-brick-secondary-fine hover:bg-gray-50'
                }`}
                onPress={() => setSelectedTemplate(template.id)}
              >
                <CardBody className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold">{template.category}</h4>
                      <p className="text-sm opacity-80">Grootboek: {template.ledger}</p>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="radio"
                        checked={selectedTemplate === template.id}
                        onChange={() => setSelectedTemplate(template.id)}
                        className="text-brick-primary"
                      />
                    </div>
                  </div>
                </CardBody>
              </Card>
            )) : (
              <div className="text-center py-8 text-neutral-secondary-mid">
                Geen templates beschikbaar
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step 2: Select Buildings */}
      {currentStep === 2 && (
        <div className="space-y-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-neutral-primary mb-2">
              Voor welke gebouwen?
            </h3>
            <p className="text-neutral-secondary-mid">Selecteer één of meerdere gebouwen</p>
          </div>

          <div className="space-y-4">
            <Input
              placeholder="Zoek gebouwen..."
              value={buildingSearchQuery}
              onChange={(e) => setBuildingSearchQuery(e.target.value)}
              startContent={<div className="text-neutral-secondary-mid">🔍</div>}
            />
            
            {filteredBuildings.length > 0 && (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="flat"
                  onPress={() => {
                    const filteredIds = filteredBuildings.map(b => b.id);
                    setSelectedBuildings([...new Set([...selectedBuildings, ...filteredIds])]);
                  }}
                  className="text-xs"
                >
                  Selecteer alle ({filteredBuildings.length})
                </Button>
                <Button
                  size="sm"
                  variant="flat"
                  color="danger"
                  onPress={() => {
                    const filteredIds = filteredBuildings.map(b => b.id);
                    setSelectedBuildings(selectedBuildings.filter(id => !filteredIds.includes(id)));
                  }}
                  className="text-xs"
                >
                  Deselecteer alle
                </Button>
              </div>
            )}

            <div className="max-h-60 overflow-y-auto space-y-2 border rounded-lg p-2">
              {filteredBuildings.length > 0 ? (
                filteredBuildings.map((building) => (
                  <Card 
                    key={building.id} 
                    isPressable
                    className={`cursor-pointer transition-all ${
                      selectedBuildings.includes(building.id)
                        ? 'bg-brick-primary text-brick-secondary-fine' 
                        : 'bg-white hover:bg-gray-50'
                    }`}
                    onPress={() => {
                      if (selectedBuildings.includes(building.id)) {
                        setSelectedBuildings(selectedBuildings.filter(id => id !== building.id));
                      } else {
                        setSelectedBuildings([...selectedBuildings, building.id]);
                      }
                    }}
                  >
                    <CardBody className="p-3">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={selectedBuildings.includes(building.id)}
                          onChange={() => {}}
                          className="text-brick-primary"
                        />
                        <div className="flex-1">
                          <div className="font-medium">{building.id}</div>
                          <div className="text-sm opacity-80">{building.googlePlaceLongAddress}</div>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                ))
              ) : (
                <div className="text-center py-8 text-neutral-secondary-mid">
                  {buildingSearchQuery ? 'Geen gebouwen gevonden voor deze zoekopdracht' : 'Geen beschikbare gebouwen'}
                </div>
              )}
            </div>

            {selectedBuildings.length > 0 && (
              <div className="p-3 bg-status-good/10 rounded-lg border border-status-good/20">
                <p className="text-status-good font-medium text-center">
                  ✓ {selectedBuildings.length} gebouw{selectedBuildings.length !== 1 ? 'en' : ''} geselecteerd
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step 3: Configure Attributes */}
      {currentStep === 3 && (
        <div className="space-y-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-neutral-primary mb-2">
              Hoeveel attributen wil je aanmaken?
            </h3>
            <p className="text-neutral-secondary-mid">Configureer de naam, afmetingen en hoeveelheid</p>
          </div>

          <div className="space-y-4">
            <Input
              label="Basis naam"
              placeholder="bijv. Garagebox, Parkeerplaats, Berging..."
              value={baseName}
              onChange={(e) => setBaseName(e.target.value)}
              isRequired
            />
            
            <Input
              label="Beschrijving (optioneel)"
              placeholder="Beschrijving van het attribuut..."
              value={baseDescription}
              onChange={(e) => setBaseDescription(e.target.value)}
            />

            {/* Dimensions */}
            <div className="space-y-4">
              <h4 className="font-medium text-neutral-primary">Afmetingen (optioneel)</h4>
              <div className="grid grid-cols-3 gap-4">
                <Input
                  label="Lengte (m)"
                  placeholder="bijv. 5.5"
                  value={dimensions.length}
                  onChange={(e) => setDimensions({...dimensions, length: e.target.value})}
                  type="number"
                  step="0.1"
                />
                <Input
                  label="Breedte (m)"
                  placeholder="bijv. 2.5"
                  value={dimensions.width}
                  onChange={(e) => setDimensions({...dimensions, width: e.target.value})}
                  type="number"
                  step="0.1"
                />
                <Input
                  label="Hoogte (m)"
                  placeholder="bijv. 2.0"
                  value={dimensions.height}
                  onChange={(e) => setDimensions({...dimensions, height: e.target.value})}
                  type="number"
                  step="0.1"
                />
              </div>
              <p className="text-xs text-neutral-secondary-mid">
                Afmetingen helpen bij het bepalen welke voertuigen geschikt zijn voor parkeerplaatsen
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex gap-4 items-end">
                <Input
                  label="Aantal attributen"
                  type="number"
                  min="1"
                  max="50"
                  value={quickCopyCount.toString()}
                  onChange={(e) => setQuickCopyCount(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-32"
                />
                
                {quickCopyCount > 1 && (
                  <>
                    <Select
                      label="Referentie type"
                      selectedKeys={[referenceType]}
                      onSelectionChange={(keys) => setReferenceType(Array.from(keys)[0] as any)}
                      className="w-40"
                    >
                      <SelectItem key="number">Nummers (1, 2, 3...)</SelectItem>
                      <SelectItem key="uppercase">Letters (A, B, C...)</SelectItem>
                      <SelectItem key="lowercase">Letters (a, b, c...)</SelectItem>
                    </Select>
                    
                    <Input
                      label="Start waarde"
                      type="number"
                      min="1"
                      value={quickCopyStartNumber.toString()}
                      onChange={(e) => setQuickCopyStartNumber(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-32"
                    />
                  </>
                )}
              </div>

              {quickCopyCount > 1 && baseName && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-medium text-blue-800 mb-2">Voorbeelden van namen:</p>
                  <div className="space-y-1">
                    {generateReferences(Math.min(5, quickCopyCount), referenceType, quickCopyStartNumber).map((ref, i) => (
                      <div key={i} className="text-sm text-blue-700">
                        • {baseName} {ref}
                      </div>
                    ))}
                    {quickCopyCount > 5 && <div className="text-sm text-blue-700">• ... en {quickCopyCount - 5} meer</div>}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Step 4: Review and Edit References */}
      {currentStep === 4 && (
        <div className="space-y-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-neutral-primary mb-2">
              Controleer en pas aan
            </h3>
            <p className="text-neutral-secondary-mid">Je kunt de referenties nog aanpassen indien nodig</p>
          </div>

          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Template:</span> {templates?.find(t => t.id === selectedTemplate)?.category || 'Onbekend'}
                </div>
                <div>
                  <span className="font-medium">Gebouwen:</span> {selectedBuildings.length}
                </div>
                <div>
                  <span className="font-medium">Basis naam:</span> {baseName}
                </div>
                <div>
                  <span className="font-medium">Aantal:</span> {attributeReferences.length}
                </div>
                {(dimensions.length || dimensions.width || dimensions.height) && (
                  <div className="col-span-2">
                    <span className="font-medium">Afmetingen:</span> 
                    {dimensions.length && ` L:${dimensions.length}m`}
                    {dimensions.width && ` B:${dimensions.width}m`}
                    {dimensions.height && ` H:${dimensions.height}m`}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <span className="text-sm font-medium">Attribuut namen (klik om te bewerken):</span>
              <div className="max-h-60 overflow-y-auto space-y-2">
                {attributeReferences && attributeReferences.length > 0 ? attributeReferences.map((ref, index) => (
                  <Input
                    key={index}
                    label={`Attribuut ${index + 1}`}
                    value={attributeReferences.length > 1 ? `${baseName} ${ref}` : baseName}
                    onChange={(e) => {
                      const newRefs = [...attributeReferences];
                      if (attributeReferences.length > 1) {
                        // Extract the reference part (everything after the base name)
                        const newValue = e.target.value.replace(`${baseName} `, '');
                        newRefs[index] = newValue;
                      } else {
                        // For single attribute, update the base name
                        setBaseName(e.target.value);
                      }
                      setAttributeReferences(newRefs);
                    }}
                    size="sm"
                  />
                )) : (
                  <div className="text-center py-4 text-neutral-secondary-mid">
                    Geen attributen om te bewerken
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </ModalBody>
    <ModalFooter>
      <div className="flex justify-between w-full">
        <Button 
          color="danger" 
          variant="light" 
          onPress={() => {
            setIsModalOpen(false);
            resetWizard();
          }}
        >
          Annuleren
        </Button>
        
        <div className="flex gap-2">
          {currentStep > 1 && (
            <Button 
              variant="flat"
              onPress={handleStepBack}
            >
              Vorige
            </Button>
          )}
          
          {currentStep < 4 ? (
            <Button
              color="primary"
              onPress={handleStepNext}
              className="bg-brick-primary text-brick-secondary-fine"
            >
              Volgende
            </Button>
          ) : (
            <Button
              color="primary"
              onPress={handleCreateAttribute}
              className="bg-brick-primary text-brick-secondary-fine"
            >
              {attributeReferences.length > 1 ? `${attributeReferences.length} attributen aanmaken` : 'Attribuut aanmaken'}
            </Button>
          )}
        </div>
      </div>
    </ModalFooter>
  </ModalContent>
</Modal>
      {/* Edit Attribute Modal */}
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} size="lg">
        <ModalContent>
          <ModalHeader>Attribuut bewerken</ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <Input
                label="Naam"
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                isRequired
              />
              
              <Input
                label="Beschrijving (optioneel)"
                value={editFormData.description}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
              />

              <div className="space-y-4">
                <h4 className="font-medium text-neutral-primary">Afmetingen (optioneel)</h4>
                <div className="grid grid-cols-3 gap-4">
                  <Input
                    label="Lengte (m)"
                    placeholder="bijv. 5.5"
                    value={editFormData.length}
                    onChange={(e) => setEditFormData({ ...editFormData, length: e.target.value })}
                    type="number"
                    step="0.1"
                  />
                  <Input
                    label="Breedte (m)"
                    placeholder="bijv. 2.5"
                    value={editFormData.width}
                    onChange={(e) => setEditFormData({ ...editFormData, width: e.target.value })}
                    type="number"
                    step="0.1"
                  />
                  <Input
                    label="Hoogte (m)"
                    placeholder="bijv. 2.0"
                    value={editFormData.height}
                    onChange={(e) => setEditFormData({ ...editFormData, height: e.target.value })}
                    type="number"
                    step="0.1"
                  />
                </div>
              </div>

              <Input
                label="Kostenplaats (Odoo)"
                placeholder="bijv. 2800-KONI-ASTR-106-PARKING"
                description="Gebruikt wanneer dit attribuut los wordt verhuurd (bv. een parking)."
                value={editFormData.odooAnalyticAccountName}
                onChange={(e) => setEditFormData({ ...editFormData, odooAnalyticAccountName: e.target.value })}
              />

              {editingAttribute && (
                <AttributeDocumentsSection attributeId={editingAttribute.id} />
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button 
              color="danger" 
              variant="light" 
              onPress={() => {
                setIsEditModalOpen(false);
                setEditingAttribute(null);
                setEditFormData({ name: '', description: '', length: '', width: '', height: '', odooAnalyticAccountName: '' });
              }}
            >
              Annuleren
            </Button>
            <Button
              color="primary"
              onPress={handleUpdateAttribute}
              className="bg-brick-primary text-brick-secondary-fine"
              isDisabled={!editFormData.name.trim()}
            >
              Bijwerken
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* Import Modal */}
      <Modal isOpen={isImportModalOpen} onClose={() => setIsImportModalOpen(false)} size="4xl">
        <ModalContent>
          <ModalHeader>Gebouw Attributen Importeren</ModalHeader>
          <ModalBody>
            {importResults ? (
              <div className="space-y-4">
                {importResults.success.length > 0 && (
                  <div>
                    <h3 className="text-lg font-medium text-status-good mb-2">
                      Succesvol geïmporteerd ({importResults.success.length})
                    </h3>
                    <div className="max-h-60 overflow-y-auto">
                      {importResults.success.map((result, index) => (
                        <div key={index} className="p-2 border border-status-good/20 rounded-sm mb-2">
                          <p className="font-medium">{result.name}</p>
                          <p className="text-sm text-neutral-secondary-mid">
                            Template: {result.savedAttribute?.template?.category}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {importResults.errors.length > 0 && (
                  <div>
                    <h3 className="text-lg font-medium text-red-600 mb-2">
                      Fouten ({importResults.errors.length})
                    </h3>
                    <div className="max-h-60 overflow-y-auto">
                      {importResults.errors.map((error, index) => (
                        <div key={index} className="p-2 border border-red-200 rounded-sm mb-2">
                          <p className="font-medium">{error.name}</p>
                          <p className="text-sm text-red-600">{error.error}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-neutral-secondary-mid">
                  Controleer de attributen voordat je ze importeert. 
                  Zorg ervoor dat de template namen exact overeenkomen met bestaande templates in het systeem.
                </p>

                <div className="max-h-96 overflow-y-auto">
                  {importedAttributes.map((attribute, index) => (
                    <div key={index} className="border rounded-lg p-4 mb-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-medium">{attribute.name}</h3>
                            <Chip
                              size="sm"
                              color={attribute.error ? 'danger' : 'success'}
                              variant="flat"
                            >
                              {attribute.error ? 'Fout' : 'Klaar'}
                            </Chip>
                          </div>

                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p><strong>Beschrijving:</strong> {attribute.description || '-'}</p>
                              <p><strong>Template:</strong> {attribute.templateName || '-'}</p>
                              <p><strong>Afmetingen:</strong> 
                                {attribute.length && ` L:${attribute.length}m`}
                                {attribute.width && ` B:${attribute.width}m`}
                                {attribute.height && ` H:${attribute.height}m`}
                                {!attribute.length && !attribute.width && !attribute.height && ' -'}
                              </p>
                            </div>
                            <div>
                              <p><strong>Gebouwen:</strong> {attribute.parsedBuildingIds?.length || 0}</p>
                              {attribute.parsedBuildingIds?.length > 0 && (
                                <p className="text-xs text-neutral-secondary-mid">
                                  {attribute.parsedBuildingIds.slice(0, 3).join(', ')}
                                  {attribute.parsedBuildingIds.length > 3 && ` +${attribute.parsedBuildingIds.length - 3} meer`}
                                </p>
                              )}
                            </div>
                          </div>

                          {attribute.error && (
                            <p className="text-red-600 text-sm mt-2">{attribute.error}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            {importResults ? (
              <>
                <Button 
                  color="secondary" 
                  variant="flat"
                  onPress={async () => {
                    try {
                      const excelResponse = await fetchWithAuth(`${API_URL}/building-attributes/generate-import-excel`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ 
                          originalAttributes: importedAttributes,
                          importResults: importResults
                        }),
                      }, session);

                      if (excelResponse.ok) {
                        const blob = await excelResponse.blob();
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        
                        const contentDisposition = excelResponse.headers.get('Content-Disposition');
                        let filename = 'building_attributes_import_results.xlsx';
                        if (contentDisposition) {
                          const matches = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                          if (matches != null && matches[1]) {
                            filename = matches[1].replace(/['"]/g, '');
                          }
                        }
                        
                        link.download = filename;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                        
                        toast.success("Excel bestand gedownload");
                      } else {
                        toast.error("Fout bij downloaden van Excel bestand");
                      }
                    } catch (error) {
                      console.error("Error downloading Excel file:", error);
                      toast.error("Fout bij downloaden van Excel bestand");
                    }
                  }}
                >
                  Download Excel met Attribuut ID's
                </Button>
                <Button 
                  color="primary" 
                  onPress={() => {
                    setIsImportModalOpen(false);
                    setImportResults(null);
                    setImportedAttributes([]);
                  }}
                >
                  Sluiten
                </Button>
              </>
            ) : (
              <>
                <Button 
                  color="danger" 
                  variant="light" 
                  onPress={() => {
                    setIsImportModalOpen(false);
                    setImportedAttributes([]);
                  }}
                >
                  Annuleren
                </Button>
                <Button
                  color="primary"
                  onPress={handleImportAttributes}
                  isDisabled={isImporting || importedAttributes.length === 0}
                  startContent={isImporting ? <Spinner size="sm" /> : undefined}
                  className="bg-brick-primary text-brick-secondary-fine"
                >
                  {isImporting ? "Importeren..." : `Importeren (${importedAttributes.length})`}
                </Button>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Choose-building modal (when an attribute is linked to several buildings) */}
      <Modal isOpen={!!buildingChoice} onClose={() => setBuildingChoice(null)} size="lg">
        <ModalContent>
          <ModalHeader>Kies het gebouw voor dit contract</ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <p className="text-neutral-secondary-mid text-sm">
                Dit attribuut is aan meerdere gebouwen gekoppeld. Selecteer het gebouw waarvoor je
                het contract wil aanmaken.
              </p>
              <div className="max-h-64 overflow-y-auto space-y-2">
                {buildingChoice?.buildings.map((b) => (
                  <Card
                    key={b.id}
                    isPressable
                    onPress={() => loadBuildingAndOpenRentModal(b.id, buildingChoice.attributeId)}
                    className="bg-white hover:bg-brick-secondary-fine transition-colors"
                  >
                    <CardBody className="p-3">
                      <div className="flex items-center gap-3">
                        <Buildings className="text-brick-primary" size={20} />
                        <p className="text-sm font-medium text-neutral-primary">
                          {b.housenr} - {b.googlePlaceLongAddress}
                        </p>
                      </div>
                    </CardBody>
                  </Card>
                ))}
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="danger" variant="light" onPress={() => setBuildingChoice(null)}>
              Annuleren
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Attribute-only rental contract modal */}
      {rentBuilding && (
        <ModalAddContract
          company={rentBuilding.company as YallyCompany}
          units={(rentBuilding.units as UnitDTO[]) || []}
          building={rentBuilding}
          isOpen={isRentModalOpen}
          onClose={handleCloseRentModal}
          refreshContracts={fetchAttributes}
          attributeOnlyDefault={true}
          prefilledBuildingAttributeId={rentAttributeId}
        />
      )}

      {/* Kostenplaats via the shared UniversalPickerModal (free-text), single row or bulk */}
      <UniversalPickerModal
        isOpen={!!kostenplaatsPicker}
        mode="text-input"
        title={
          kostenplaatsPicker?.mode === 'single'
            ? `Kostenplaats voor "${kostenplaatsPicker.attribute.name}"`
            : `Kostenplaats instellen voor ${selectedRows.size} attribu${selectedRows.size === 1 ? 'ut' : 'ten'}`
        }
        onClose={() => !isBulkKostenplaatsSaving && setKostenplaatsPicker(null)}
        onConfirm={(value: string) => handleKostenplaatsPickerConfirm(value)}
        config={{
          searchPlaceholder: 'bijv. 8800-STAT-67-PARKINGS',
          defaultValue:
            kostenplaatsPicker?.mode === 'single'
              ? kostenplaatsPicker.attribute.odooAnalyticAccountName || ''
              : '',
        }}
      />

      {/* Cancel (opzeggen) confirmation for an attribute-only contract */}
      <Modal
        isOpen={!!cancelTarget}
        onClose={() => {
          if (isCancelling) return;
          resetCancelState();
        }}
        size="md"
      >
        <ModalContent>
          <ModalHeader>Contract opzeggen</ModalHeader>
          <ModalBody>
            {isCheckingCancel ? (
              <div className="flex items-center gap-3 py-2">
                <Spinner size="sm" />
                <span className="text-neutral-secondary-mid text-sm">Contract controleren…</span>
              </div>
            ) : cancelEligibility && !cancelEligibility.cancellable ? (
              // Blocked: the contract has a unit or other products.
              <div className="flex flex-col gap-2 p-3 rounded-lg bg-status-bad/10 border border-status-bad/30">
                <p className="text-sm font-medium text-status-bad">Opzeggen niet mogelijk hier</p>
                <p className="text-sm text-neutral-primary">{cancelEligibility.reason}</p>
                {cancelEligibility.buildingId && (
                  <a
                    href={`/building/${cancelEligibility.buildingId}/verhuur`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-moss-primary underline hover:text-moss-primary/80 w-fit"
                  >
                    Open de Verhuur tab van gebouw {cancelEligibility.buildingId} →
                  </a>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                <p className="text-neutral-primary">
                  Hoe wil je het contract voor <strong>{cancelTarget?.attributeName}</strong> opzeggen?
                </p>
                <div className="flex flex-col gap-1">
                  <Input
                    type="date"
                    label="Einddatum (opzeg ingaan)"
                    description="Kies een datum nu of in de toekomst. Op die datum eindigt het contract."
                    value={cancelEndDate}
                    onChange={(e) => setCancelEndDate(e.target.value)}
                  />
                </div>
                <p className="text-xs text-neutral-secondary-mid">
                  Of beëindig het contract meteen (sluit het contract per direct, ook in Odoo).
                  Bevat het contract meerdere producten of een unit, dan kan het enkel via de Verhuur tab
                  in het gebouwdetailscherm worden opgezegd.
                </p>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={resetCancelState} isDisabled={isCancelling}>
              {cancelEligibility && !cancelEligibility.cancellable ? 'Sluiten' : 'Annuleren'}
            </Button>
            {!(cancelEligibility && !cancelEligibility.cancellable) && !isCheckingCancel && (
              <>
                <Button
                  color="danger"
                  variant="flat"
                  onPress={handleCancelImmediately}
                  isLoading={isCancelling && !cancelEndDate}
                  isDisabled={isCancelling}
                >
                  Meteen beëindigen
                </Button>
                <Button
                  color="primary"
                  className="bg-moss-primary text-brick-secondary-fine"
                  onPress={handleCancelInFuture}
                  isLoading={isCancelling && !!cancelEndDate}
                  isDisabled={isCancelling || !cancelEndDate}
                >
                  Laat opzeg ingaan
                </Button>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}; 